import { AvatarGroup, Box, Chip, CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { graphql } from '../../gql';
import { useApiQuery } from '../../query/hooks';
import { PersonaAvatarSmall } from '../../persona/PersonaAvatar';
import { useNavigate } from 'react-router';
import { LinkRounded, PeopleAltOutlined, ExploreOutlined } from '@mui/icons-material';
import { ChipStyles } from './JourneyChip.styles';
import getOodColor from '../../utils/getOodColor';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';

const personaDetail_getJourneyDetail = graphql(`
	query personaDetail_getJourneyDetail($id: UUID!) {
		getJourney(by: { id: $id }) {
			id
			name
			description
			color
			personas {
				id
				...PersonaAvatar_persona
			}
			teams {
				id
				name
			}
		}
	}
`);
const personaDetail_personaUsedInJourney = graphql(`
	query personaDetail_personaUsedInJourney($personaId: UUID!, $journeyId: UUID!) {
		getPersona(by: { id: $personaId }) {
			activities(filter: { touchpoint: { phase: { journey: { id: { eq: $journeyId } } } } }) {
				id
				name
			}
		}
	}
`);

type Props = Readonly<{
	journeyId: string;
	personaId: string;
}>;

const JourneyBlockTile: FC<Props> = ({ journeyId, personaId }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { data: journey, isLoading } = useApiQuery(personaDetail_getJourneyDetail, { variables: { id: journeyId } });
	const { data: usedInJourney } = useApiQuery(personaDetail_personaUsedInJourney, { variables: { personaId, journeyId } });

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (journey?.getJourney == null) {
		return null;
	}

	const { getJourney } = journey;

	const activityLocations = usedInJourney?.getPersona?.activities.map((activity, i) => {
		const isLast = usedInJourney.getPersona?.activities != null && i === usedInJourney.getPersona.activities.length - 1;

		return (
			<Link
				href={journeyId && `/journeys/${journeyId}/detail?activity=${activity.id}`}
				variant={'button'}
				key={activity.id}
				sx={{
					textDecoration: 'none',
					cursor: 'pointer',
					':hover': { textDecoration: 'underline' },
					':after': { content: isLast ? '""' : "', '" },
				}}
			>
				{activity.name}
			</Link>
		);
	});

	return (
		<Stack
			direction={'row'}
			padding={2.5}
			paddingY={1.875}
			borderRadius={2}
			border={`1px solid ${theme.palette.grey[300]}`}
			justifyContent={'space-between'}
			alignItems={'center'}
			boxShadow={theme.customShadows.card}
			position={'relative'}
			sx={{
				'&:hover': {
					borderColor: theme.palette.grey[700],
					cursor: 'pointer',
				},
			}}
			onClick={() => navigate(`/journeys/${getJourney.id}/activity-map`)}
		>
			<Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={2}>
				<ExploreOutlined sx={{ color: theme.palette.grey[500] }} />
				<Typography fontWeight={'500'} fontSize={'14px'}>
					{getJourney.name}
				</Typography>

				<Typography fontWeight={'500'} fontSize={'14px'} sx={{ ':before': { content: "'('" }, ':after': { content: "')'" } }}>
					{activityLocations}
				</Typography>
			</Stack>

			<Stack direction={'row'} alignItems={'center'} gap={2.5}>
				<Stack direction={'row'} flexWrap={'wrap'} justifyContent={'end'} gap={1}>
					{getJourney.teams.map((team) => (
						<Chip
							key={team.id}
							icon={<PeopleAltOutlined sx={{ fill: getOodColor(theme, getJourney.color).content, fontSize: '16px' }} />}
							label={team.name}
							variant={'outlined'}
							sx={{
								...ChipStyles(getOodColor(theme, getJourney.color)),
							}}
						/>
					))}
				</Stack>
				<AvatarGroup
					max={3}
					sx={{
						'.MuiAvatarGroup-avatar': {
							fontSize: (theme) => theme.typography.body2.fontSize,
							width: 24,
							height: 24,
							border: 'none',
							backgroundColor: 'transparent',
							color: (theme) => theme.palette[getNightOrDayPalette(theme)][600],
							pt: 0.25,
							pl: 0.5,
						},
					}}
				>
					{getJourney.personas && getJourney.personas.map((persona) => <PersonaAvatarSmall key={persona.id} persona={persona} />)}
				</AvatarGroup>
				<IconButton size={'small'} onClick={(e) => e.stopPropagation()}>
					<LinkRounded sx={{ color: theme.palette.grey[500] }} />
				</IconButton>
			</Stack>
		</Stack>
	);
};

export default JourneyBlockTile;
